import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ClearAuthToken, getAuthToken, SetAuthToken } from '../state';
import { DataSanitizerService } from '@libraries/data-sanitizer';
import {
  getContractorTokenFromCookie,
  getStoredAuthToken,
  getStoredUserType,
  handleContractorLogout,
  isContractorHost,
  isContractorLoggedOut,
  isContractorSession,
} from '@libraries/helpers';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor(private store: Store, private dataSanitizerService: DataSanitizerService) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.store.select(getAuthToken).pipe(
      take(1),
      mergeMap((authentication) => {
        let token = authentication;
        const isContractor =
          isContractorSession() ||
          getStoredUserType() === 'contractor' ||
          (typeof sessionStorage !== 'undefined' &&
            (sessionStorage.getItem('apply_user_type') === 'contractor' ||
              sessionStorage.getItem('is_contractor_session') === 'true'));

        if (isContractor) {
          if (isContractorLoggedOut()) {
            this.store.dispatch(ClearAuthToken());
            handleContractorLogout('logged_out_in_other_tab');
            return throwError(() => new Error('Contractor logged out in other tab'));
          }
          const cookieToken = getContractorTokenFromCookie();
          if (cookieToken) {
            token = cookieToken;
          }
        }

        if (!token) {
          token = getStoredAuthToken() || undefined;
          if (token) {
            const userType = getStoredUserType() || undefined;
            this.store.dispatch(SetAuthToken({ authToken: token, userType }));
          }
        }

        // Sanitize the request body
        if (
          request.body &&
          !(request.body instanceof FormData) &&
          request.headers.get(this.dataSanitizerService.SKIP) !== 'true'
        ) {
          request = request.clone({
            body: this.dataSanitizerService.sanitizeData(request.body),
          });
        }

        if (
          token &&
          (request.url.startsWith(environment.legacyApi.host) ||
            request.url.startsWith(environment.legacyApi.base) ||
            request.url.startsWith(environment.managerClientConfig.baseUrl) ||
            request.url.includes('/application_process_v4') ||
            request.url.includes('/property_owners') ||
            request.url.includes('/api/'))
        ) {
          request = request.clone({
            setHeaders: {
              authentication: token,
              Authentication: token,
            },
          });
        }

        return next.handle(request).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 401 || error.status === 403) {
              const userType = getStoredUserType();
              if (userType === 'contractor' || isContractorSession()) {
                this.store.dispatch(ClearAuthToken());
                handleContractorLogout('unauthorized');
              }
            }
            return throwError(() => error);
          })
        );
      })
    );
  }
}
